import { useLayoutEffect, useRef, useState } from "react";

// The largest a key may grow (px per 1u). The surface is fit-and-centred, so on a wide window the
// keyboard stops growing once it hits this ceiling and the surplus width becomes empty side bands.
// Raised from 60 so the render fills more of a wide canvas before the gaps appear (the fit-to-box
// width/height terms still cap growth first on normal windows, so this never overflows).
const MAX_UNIT = 76;

/**
 * One key unit for every canvas surface: sized so the board and the mouse map
 * fit the wrap together (the mouse scales with the board, design v2 section 5).
 * Measured once on the wrap, not per surface: the mouse map's width in units
 * depends only on chip reservation, never on the unit, so the measurement
 * cannot feed back into itself.
 */
export function useSurfaceUnit(widthUnits: number, heightUnits: number, reservePx = 0) {
  const ref = useRef<HTMLDivElement>(null);
  const [unit, setUnit] = useState(46);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const w = el.clientWidth - 32;
      // reservePx is the fixed (unit-independent) chrome the wrap holds beside the boards - in compare,
      // the two stacked header cards and the inter-row gaps. It is reserved in PIXELS, not scaled into
      // heightUnits: the cards are fixed-height, so a unit-scaled reserve under-counts them as the unit
      // shrinks and the bottom board spills off-screen. A fixed reserve also breaks the feedback a
      // *measured* header height would create (the card width tracks the plate width = board.width *
      // unit, so its wrap height would depend on the very unit we are solving for).
      const h = el.clientHeight - 16 - reservePx;
      setUnit(
        Math.max(14, Math.min(MAX_UNIT, Math.floor(Math.min(w / widthUnits, h / heightUnits)))),
      );
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [widthUnits, heightUnits, reservePx]);

  return { ref, unit };
}
